import { useCallback, useEffect, useMemo, useRef } from "react";
import { useOffers } from "@hooks/useOffers";
import { getIsAddingBroadbandOfferAllowed, selectOfferByServiceGroup } from "@utils/offerUtils";
import { IEirSalesPrequal } from "@modules/eirSalesFacade/interfaces/models/IEirSalesPrequal";
import { clearBroadbandEmailSent, removeItemFromStorage, savePrequal } from "@utils/storage";
import { useHistory } from "react-router-dom";
import { EServiceGroup, ROUTES } from "@constants";
import { useScroller } from "@hooks/useScroller";

export const useHomeBroadband = () => {
    const { push } = useHistory();
    const {
        offerList,
        offerListState,
        getOfferList,
        createProspectState,
        callCreateProspect,
        cart,
        cartState,
        getCart,
    } = useOffers();
    const eirCodeFieldRef = useRef<HTMLInputElement | null>(null);
    const { scrollToTop } = useScroller();

    useEffect(() => {
        removeItemFromStorage("prequal");
        clearBroadbandEmailSent();
    }, []);

    const setPrequal = useCallback(
        (prequal: IEirSalesPrequal) => {
            savePrequal(prequal);
            push(ROUTES.BROADBAND_RESULT_PAGE_URL);
        },
        [push]
    );

    const broadbandOffer = useMemo(() => selectOfferByServiceGroup(offerList, EServiceGroup.SA_BB), [offerList]);
    const allowAddingBroadbandOffer = useMemo(() => cart && getIsAddingBroadbandOfferAllowed(cart), [cart]);

    const isLoadingList = [offerListState.isLoading, cartState.isLoading, createProspectState.isLoading];
    const isLoading = isLoadingList.some((loading) => loading);
    const errorOptions = [offerListState.error, cartState.error, createProspectState.error];
    const error = errorOptions.find((isError) => isError);

    const getRetryFunction = useCallback(() => {
        if (offerListState.error) {
            return getOfferList;
        }
        if (cartState.error) {
            return getCart;
        }
        if (createProspectState.error) {
            return callCreateProspect;
        }

        return () => window.location.reload();
    }, [cartState.error, createProspectState.error, offerListState.error, callCreateProspect, getCart, getOfferList]);

    const handleBannerButtonClick = () => {
        eirCodeFieldRef.current?.focus();
        scrollToTop();
    };

    return {
        setPrequal,
        broadbandOffer,
        isLoading,
        error,
        allowAddingBroadbandOffer,
        retry: getRetryFunction(),
        eirCodeFieldRef,
        handleBannerButtonClick,
    };
};
